/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {FC, memo, useCallback, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import Badge from '@material-ui/core/Badge';
import {makeStyles} from '@material-ui/core/styles';
import classnames from 'classnames';
import {translate} from '@ion/core/src/locale/locale';
import {CVSAssetType} from '@ion/core/src/types/entities';
import Avatar from '@ion/dls/src/components/avatar/Avatar';
import {IconButton} from '@ion/dls/src/components/buttons/IconButton';
import ClickAwayListener from '@ion/dls/src/components/listeners/ClickAwayListener';
import Tooltip from '@ion/dls/src/components/tooltip/Tooltip';
import {useToggleToolTip} from '@ion/dls/src/hooks/useToggleToolTip';
import {TopNavNotificationIcon} from '@ion/dls/src/icons/TopNavNotificationIcon';
import Render from '@ion/dls/src/render/Render';
import {dlsAlt, dlsColor, dlsShadow} from '@ion/dls/src/theme/theme';
import ThemeModeToggle from '@ion/dls/src/theme/ThemeModeToggle';
import useThemeContext from '@ion/dls/src/theme/useThemeContext';
import {HeaderTypography} from '@ion/dls/src/typography/HeaderTypography';
import {hasAppNotificationAccess} from '@/security/Features';
import {canToggleThemeMode} from '@/security/themeAccess';
import {setNotificationsPanelStatus} from '@/store/actions/notifications';
import {usernameSelector} from '@/store/selectors/login-selectors';
import {currentPageSelector} from '@/store/selectors/nav-selectors';
import {
  isPanelOpenSelector,
  unreadCountSelector
} from '@/store/selectors/notifications-selectors';
import UserProfileMobile from '../components/profile/UserProfileMobile';
import {getPageTitle} from '../routes/RouteDefinitions';
import {toggleUserProfileView} from '../store/actions/nav';
import {DesktopHeaderProps} from './DesktopHeader';
import MobileHeaderSearch from './MobileHeaderSearch';
import styles from './MobileHeader.module.scss';

export interface MobileHeaderProps extends DesktopHeaderProps {
  searchResults?: CVSAssetType[];
  searchTerm?: string;
  currentPage?: string;
  isSearching?: boolean;
  dialogOpen?: boolean;
  handleClickOpen?: () => void;
  setDialogOpen?: Function;
  onSearch?: Function;
  onHelp?: Function;
  onNavigation?: Function;
}

const useStyles = makeStyles((theme) => ({
  profileTitle : {
    width     : '100%',
    textAlign : 'center',
    marginTop : 6
  },
  ionLink : {
    ...dlsAlt('components', 'ionToggle', 'ionBeta')
  },
  betaLink : {
    border          : `4px solid ${dlsColor('caBlue')}`,
    backgroundColor : dlsColor('caBlue'),
    borderRadius    : '20px',
    ...dlsAlt('components', 'ionpreviewText', 'preview')
  },
  ionBeta : {
    marginLeft : '-88px',
    display    : 'flex',
    alignItems : 'center'
  },
  notificationBadgeMobile : {
    ...dlsAlt('components', 'badge')
  },
  notificationBadgeMobileAnchor : {
    top   : '25%',
    right : '30%'
  },
  // current-theme Top Navigation (Figma "Navigation/Secondary/Mweb"):
  // 16px side padding, 44x44 targets, 8px gap between bell and avatar ...
  currentContainer : {
    '&&' : {
      padding : '0 16px'
    }
  },
  currentRightContent : {
    alignItems : 'center',
    display    : 'flex',
    gap        : 8
  },
  currentTouchTarget : {
    alignItems              : 'center',
    background              : 'none',
    border                  : 0,
    boxSizing               : 'border-box',
    cursor                  : 'pointer',
    display                 : 'flex',
    height                  : 44,
    justifyContent          : 'center',
    margin                  : 0,
    padding                 : 10,
    WebkitTapHighlightColor : 'transparent',
    width                   : 44,
    '&:focus-visible'       : {
      borderRadius  : 8,
      outline       : `2px solid ${dlsColor('aaBlue')}`,
      outlineOffset : -2
    }
  },
  currentIconFrame : {
    alignItems     : 'center',
    borderRadius   : 4,
    display        : 'flex',
    height         : 24,
    justifyContent : 'center',
    width          : 24
  },
  currentIconFramePressed : {
    backgroundColor : dlsAlt('colors', 'paleBlue2'),
    boxShadow       : dlsShadow(3)
  },
  // the dark mode toggle's icon takes its frame's color, as the bell's does ...
  currentThemeModeIcon : {
    color : dlsColor('grey1')
  },
  currentThemeModeIconPressed : {
    color : dlsColor('darkTeal')
  },
  // `&&` + !important: Avatar's own (dynamic) root class sets grey5 ...
  currentAvatar : {
    '&&' : {
      backgroundColor : `${dlsColor('pastelTeal')} !important`,
      color           : dlsColor('grey1'),
      fontFamily      : 'Inter',
      fontWeight      : 600,
      letterSpacing   : '0.5px',
      lineHeight      : '16px',
      margin          : 0
    }
  },
  currentAvatarPressed : {
    '&&' : {
      backgroundColor : `${dlsColor('paleBlue')} !important`,
      boxShadow       : dlsShadow(3)
    }
  }
}));

type PressedTarget = 'notification' | 'avatar' | 'themeMode' | null;

const iconsTooltip = makeStyles((theme) => ({
  arrow : {
    color : dlsColor('grey1')
  },
  tooltip : {
    ...dlsAlt('components', 'iconTooltip')
  }
}));

/**
 * @MobileHeader
 */
const MobileHeader: FC<MobileHeaderProps> = ({
  onContactSupportOpen,
  isAccountRestricted
}) => {
  const canAccessNotification          = hasAppNotificationAccess();
  const canToggle                      = canToggleThemeMode();
  const classes                        = useStyles();
  const dispatch                       = useDispatch();
  const [isUserProfile, setIsUserProfile] = useState(false);
  const notificationCount              = useSelector(unreadCountSelector);
  const notificationPanelOpen          = useSelector(isPanelOpenSelector);
  const username                       = useSelector(usernameSelector);
  const currentPage                    = useSelector(currentPageSelector);
  const [tooltipId, toggleToolTip]     = useToggleToolTip(null);
  const {isCurrent}                    = useThemeContext();
  // Figma Pressed is only shown while the finger/pointer is down ...
  const [pressedTarget, setPressedTarget] = useState<PressedTarget>(null);
  const clearPressed                      = () => setPressedTarget(null);
  const getPressHandlers                  = (target: PressedTarget) => ({
    onPointerDown   : () => setPressedTarget(target),
    onPointerUp     : clearPressed,
    onPointerLeave  : clearPressed,
    onPointerCancel : clearPressed
  });
  // the toggle is memoized, so its props hold between renders: the handlers
  // only set the pressed state, which is stable, and its classes change only
  // as its own press does ...
  const isThemeModePressed   = pressedTarget === 'themeMode';
  const themeModeButtonProps = useMemo(() => getPressHandlers('themeMode'), []);
  const themeModeClasses     = useMemo(() => ({
    root : classes.currentTouchTarget,
    icon : classnames(classes.currentIconFrame, classes.currentThemeModeIcon, {
      [classes.currentIconFramePressed]     : isThemeModePressed,
      [classes.currentThemeModeIconPressed] : isThemeModePressed
    })
  }), [classes, isThemeModePressed]);
  // pointer (tap/click) activations drop focus so the button doesn't keep a
  // focus ring when a panel closes and restores focus to it; keyboard
  // activations (event.detail === 0) keep focus for accessibility ...
  const onPointerActivate = (action: () => void) => (event: React.MouseEvent<HTMLButtonElement>) => {
    if (event.detail > 0) {
      event.currentTarget.blur();
    }
    action();
  };

  const onUserProfileClick = useCallback(function() {
    setIsUserProfile(true);
    dispatch(toggleUserProfileView(true));
  }, []);

  function handleClose() {
    setIsUserProfile(false);
    dispatch(toggleUserProfileView(false));
  }

  function handleTooltipClose() {
    toggleToolTip('null');
  };

  const toggleNotificationPanel = useCallback(function() {
    dispatch(setNotificationsPanelStatus(!notificationPanelOpen));
  }, [notificationPanelOpen]);

  function IconCustomTooltip(props: any) {
    const title=translate('nav:underDevelopment.title');
    return (
      <ClickAwayListener onClickAway={handleTooltipClose}
        mountListener>
        <Tooltip title={title}
          arrow
          classes={iconsTooltip()}
          open={tooltipId === props?.id}
          {...props} />
      </ClickAwayListener>
    );
  }

  return (
    <div className={classnames(styles.flexContainer, {[classes.currentContainer]: isCurrent})}
      style={{borderBottom: isUserProfile ? `1px solid ${dlsColor('grey5')}` : 'initial'}}>
      <>
        <MobileHeaderSearch />
        <Render.If condition={Boolean(currentPage)}>
          <HeaderTypography variant='h2' className={classes.profileTitle}>
            {getPageTitle(currentPage)}
          </HeaderTypography>
        </Render.If>
        <Render.If condition={isCurrent}>
          <div className={classes.currentRightContent}>
            <ThemeModeToggle canToggle={canToggle}
              slot='header'
              buttonProps={themeModeButtonProps}
              classes={themeModeClasses} />
            <Render.If condition={!isAccountRestricted && canAccessNotification}>
              <button type='button'
                aria-label={translate('nav:navigation.alerts')}
                className={classes.currentTouchTarget}
                onClick={onPointerActivate(toggleNotificationPanel)}
                {...getPressHandlers('notification')}>
                <span className={classnames(classes.currentIconFrame, {
                  [classes.currentIconFramePressed] : pressedTarget === 'notification'
                })}>
                  <Badge badgeContent={notificationCount}
                    classes={{badge: classes.notificationBadgeMobile}}
                    color='error'
                    max={99}
                    overlap='rectangular'>
                    <TopNavNotificationIcon color={pressedTarget === 'notification'
                      ? dlsColor('darkTeal')
                      : dlsColor('grey1')} />
                  </Badge>
                </span>
              </button>
            </Render.If>
            <button type='button'
              aria-label={username}
              className={classes.currentTouchTarget}
              onClick={onPointerActivate(onUserProfileClick)}
              {...getPressHandlers('avatar')}>
              <Avatar className={classnames(classes.currentAvatar, {
                [classes.currentAvatarPressed] : pressedTarget === 'avatar'
              })}
              name={username} />
            </button>
          </div>
        </Render.If>
        <Render.If condition={!isCurrent}>
          <div className={styles.rightContent}>
            {/*
            [ION-APP TODO]: Restore message icon when chat is available
            See the following link for the code to be restored:
            https://bitbucket.org/dl-itendusersupport/ion-monorepo/src/master/packages/ion-suite/src/components/header/MobileHeader.tsx?at=v.ion-suite-3.0.7#155
          */}
            <Render.If condition={!isAccountRestricted && canAccessNotification}>
              <div className={styles.rightSubContent}>
                <div onClick={toggleNotificationPanel}>
                  <Badge badgeContent={notificationCount}
                    classes={{
                      badge                        : classes.notificationBadgeMobile,
                      anchorOriginTopRightCircular : classes.notificationBadgeMobileAnchor
                    }}
                    color='error'
                    max={99}
                    overlap='circular'>
                    <IconButton disableRipple
                      isSelected={notificationPanelOpen}
                      icon={<TopNavNotificationIcon />} />
                  </Badge>
                </div>
              </div>
            </Render.If>
            <span className={styles.rightSubContent}>
              <Avatar className={styles.avatar}
                name={username}
                onClick={onUserProfileClick} />
            </span>
          </div>
        </Render.If>
      </>
      <UserProfileMobile showProfile={isUserProfile}
        handleClose={handleClose}
        onContactSupportOpen={onContactSupportOpen} />
    </div>
  );
};
export default memo(MobileHeader);
